import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import DOMPurify from 'dompurify';
import qs from 'qs';
import configData from './WEBMAP_config.json';

mapboxgl.accessToken = 'pk.eyJ1IjoibXBhdGxhcyIsImEiOiJja2VlbDgycmMwNHg1Mnludm9kYWwyOTNqIn0.octDl8P_21_jRQbmbj9VmQ';

export default function App() {
  const defaultLayers = useRef(null);

  if (!defaultLayers.current) {
    let layers = ['initiatives'];
    let layerparam = qs.parse(window.location.search, { ignoreQueryPrefix: true }).layers;
    if (layerparam) {
      layers = layerparam.split(',').filter((x) => x !== '');
    }
    console.log('defaultLayers:', layers);
    defaultLayers.current = layers;
  }

  const mapContainer = useRef(null);
  const map = useRef(null);
  const sidebarContentRef = useRef();
  const hoverInitiativeId = useRef(null);
  const hoverExpeditionId = useRef(null);
  const hoverGrantId = useRef(null);
  const [initiativesChecked, setInitiativesChecked] = useState(defaultLayers.current.includes('initiatives'));
  const [initiativesLoaded, setInitiativesLoaded] = useState(false);
  const [grantsChecked, setGrantsChecked] = useState(defaultLayers.current.includes('grants'));
  const [grantsLoaded, setGrantsLoaded] = useState(false);
  const [expeditionsChecked, setExpeditionsChecked] = useState(defaultLayers.current.includes('expeditions'));
  const [expeditionsLoaded, setExpeditionsLoaded] = useState(false);
  const [allLayersLoaded, setAllLayersLoaded] = useState(false);
  const [zoomChecked, setZoomChecked] = useState(true);

  const lng = -170;
  const lat = 12;
  const zoom = 0.7;

  const welcomeCategory = configData.default_category;
  const welcomeTitle = configData.default_headline;
  const welcomeDescription = configData.default_content;
  const welcomeImg = configData.default_img || null;
  const welcomeUrl = configData.default_url || null;

  const [category, setCategory] = useState(welcomeCategory);
  const [title, setTitle] = useState(welcomeTitle);
  const [description, setDescription] = useState(welcomeDescription);
  const [dates, setDates] = useState(null);
  const [featuredImg, setFeaturedImg] = useState(welcomeImg);
  const [actionUrl, setActionUrl] = useState(welcomeUrl);

  const [eezIso3List, setEezIso3List] = useState(
    // ['MDV', 'TON', 'WSM', 'BMU', 'Azores', 'ATG', 'CUW', 'FSM', 'MSR', 'FJI']
    ['BMU', 'ATG']
  );

  const [legacyIso3List, setLegacyIso3List] = useState(
    // ['ATG', 'CUW', 'MSR']
    []
  );

  const tileHost = 'https://staging.mpatlas.org/services';

  const mpaStatic = {
    id: 'mpaStatic',
    type: 'vector',
    tiles: [tileHost + '/mpas/tiles/{z}/{x}/{y}.pbf'],
    // maxzoom: 10,
    promoteId: 'id',
  };

  const eezStatic = {
    id: 'eezStatic',
    type: 'vector',
    tiles: [tileHost + '/eez/tiles/{z}/{x}/{y}.pbf'],
    // maxzoom: 11,
    promoteId: 'id',
  };

  const eezAzoresStatic = {
    // id: 'eezAzoresStatic',
    type: 'geojson',
    generateId: true,
    data: 'geojson/WEBMAP_azores_eez_simple.geojson',
  };

  const eezFilter = (eezs = eezIso3List) => {
    return (
      ['any',
        ['in', ['get', 'sov1'], ['literal', eezs]],
        ['in', ['get', 'sov2'], ['literal', eezs]],
        ['in', ['get', 'sov3'], ['literal', eezs]],
        ['in', ['get', 'ter1'], ['literal', eezs]],
        ['in', ['get', 'ter2'], ['literal', eezs]],
        ['in', ['get', 'ter3'], ['literal', eezs]],
        ['in', ['get', 'TERRITORY1'], ['literal', eezs]],
      ]
    );
  };

  const eezFillStyle = () => {
    return (
      {
        'fill-color': [
          'case',
          eezFilter(legacyIso3List),
          '#4569a2',
          '#00b9d1'
        ],
        // 'fill-outline-color': '#000000',
        'fill-opacity': {
          stops: [
            [1, 0.2],
            [12, 0],
          ],
        },
      }
    )
  };

  const eezLineStyle = () => {
    return (
      {
        'line-width': 1,
        // 'line-color': '#00b9d1',
        'line-color': [
          'case',
          eezFilter(legacyIso3List),
          '#294785',
          '#0A425F'
        ],
        'line-dasharray': [2.5, 1.2],
      }
    )
  };

  const eezGlobalLandPoly = {
    id: 'eez-global-land-poly',
    source: 'eezStatic',
    'source-layer': 'eez_land',
    minzoom: 0,
    maxzoom: 12,
    // type, filter, paint provided by consumer
    before: 'country-boundaries overlay',
    type: 'fill',
    filter: eezFilter(),
    paint: eezFillStyle()
  }

  const eezGlobalLandLine = {
    id: 'eez-global-land-line',
    source: 'eezStatic',
    'source-layer': 'eez_land',
    minzoom: 0,
    maxzoom: 12,
    // type, filter, paint provided by consumer
    before: 'country-boundaries overlay',
    type: 'line',
    filter: eezFilter(),
    paint: eezLineStyle()
  }

  const eezAzoresLandPoly = {
    id: 'eez-azores-land-poly',
    source: 'eezAzoresStatic',
    minzoom: 0,
    maxzoom: 12,
    before: 'eez-global-land-poly overlay',
    type: 'fill',
    filter: eezFilter(),
    paint: eezFillStyle()
  };

  const eezAzoresLandLine = {
    id: 'eez-azores-land-line',
    source: 'eezAzoresStatic',
    minzoom: 0,
    maxzoom: 12,
    before: 'eez-global-land-line overlay',
    type: 'line',
    filter: eezFilter(),
    paint: eezLineStyle()
  };

  const mpaGlobalPoly = {
    id: 'mpa-global-poly',
    source: 'mpaStatic',
    'source-layer': 'mpa_poly',
    minzoom: 0,
    // type, filter, paint provided by consumer
    // before: 'land-structure-polygon',
    type: 'fill',
    filter: ['==', ['get', 'is_mpa'], true],
    paint: {
      'fill-outline-color': '#666',
      'fill-color': '#0296a0',
      'fill-opacity': {
        stops: [
          [1, 0.75],
          [5, 0.5],
          [10, 0.2],
        ],
      },
    },
  };

  const addPopup = (el, lat, lng) => {
    const placeholder = document.createElement('div');
    const root = createRoot(placeholder);
    root.render(el);

    if (lat > 90) {
      lat = 89.9;
    }
    if (lat < -90) {
      lat = -89.9;
    }

    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: [0, -30]
    })
      .setDOMContent(placeholder)
      .setLngLat({ lng: lng, lat: lat })
      .addTo(map.current);
    return (popup);
  }

  useEffect(() => {
    if (map.current) return; // initialize map only once
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mpatlas/ckoemr719410x17le56tqhbm7',
      center: [lng, lat],
      zoom: zoom
    });
    map.current.fitBounds([
      [30, -60], // southwestern corner of the bounds
      [380, 60] // northeastern corner of the bounds
    ]);
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-left');
    if (!zoomChecked) {
      map.current.scrollZoom.disable();
    }


    map.current.on('load', function () {
      map.current.addSource(eezStatic.id, eezStatic);
      map.current.addSource('eezAzoresStatic', eezAzoresStatic);
      map.current.addSource(mpaStatic.id, mpaStatic);

      map.current.addLayer(eezGlobalLandPoly);
      map.current.addLayer(eezAzoresLandPoly);
      map.current.addLayer(eezGlobalLandLine);
      map.current.addLayer(eezAzoresLandLine);
      // map.current.addLayer(mpaGlobalPoly)

      map.current.addSource('initiatives', {
        type: 'geojson',
        generateId: true,
        data: 'geojson/WEBMAP_waitt_institute_projects.geojson'
      });
      map.current.addSource('grants', {
        type: 'geojson',
        generateId: true,
        data: 'geojson/WEBMAP_waitt_roc_grants.geojson'
      });
      map.current.addSource('expeditions', {
        type: 'geojson',
        // generateId: true,
        data: 'geojson/WEBMAP_waitt_expeditions_point.geojson'
      });
      map.current.addSource('expeditions-tracks', {
        type: 'geojson',
        // generateId: true,
        data: 'geojson/WEBMAP_waitt_expeditions_line.geojson'
      });

      map.current.addLayer({
        'id': 'expeditions-tracks',
        'type': 'line',
        'source': 'expeditions-tracks', // reference the data source
        'layout': {
          'visibility': (expeditionsChecked) ? 'visible' : 'none',
          // 'line-join': 'round',
          // 'line-cap': 'round'
        },
        'paint': {
          'line-color': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            '#f4b202',
            '#14386a'
          ],
          'line-width': 1,
          'line-opacity': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            1,
            0.6
          ]
        }
      });

      // Load "initiative" marker
      map.current.loadImage(
        'icons/marker-blue2-medium.png',
        function (error, image) {
          if (error) throw error;
          map.current.addImage('initiative-marker', image);
          // Load "legacy area" marker
          map.current.loadImage(
            'icons/marker-blue-legacy.png',
            function (error, image) {
              if (error) throw error;
              map.current.addImage('initiative-legacy-marker', image);
              map.current.addLayer({
                'id': 'initiatives',
                'type': 'symbol',
                'source': 'initiatives', // reference the data source
                'layout': {
                  'visibility': (initiativesChecked) ? 'visible' : 'none',
                  'icon-image': [
                    'case',
                    ['in', ['get', 'legacy'], ['literal', ['legacy', 'pilot']]],
                    'initiative-legacy-marker',
                    'initiative-marker'
                  ],
                  'icon-allow-overlap': true,
                  'icon-ignore-placement': true,
                  'icon-size': 0.055,
                  'icon-anchor': 'bottom',
                  // get the title name from the source's "title" property
                  'text-field': ['get', 'title'],
                  'text-font': [
                    'Montserrat Regular',
                    'Open Sans Regular',
                    'Arial Unicode MS Regular'
                  ],
                  'text-size': {
                    "stops": [
                      [0, 10],
                      [1, 12],
                      [2, 13]
                    ],
                  },
                  // default 'text-offset': [0.7, -0.1]
                  // 'label_offset' property set to null if x and y are not defined
                  'text-offset': ['coalesce', ['get', 'label_offset'], ['literal', [0.7, -0.1]]],
                  'text-anchor': [
                    'case',
                    ['!=', ['get', 'label_anchor'], ''],
                    ['coalesce', ['get', 'label_anchor'], 'left'],
                    'left'
                  ],
                  // 'text-allow-overlap': true,
                  'text-ignore-placement': true,
                  'text-optional': true,
                },
                'paint': {
                  'icon-opacity': [
                    'case',
                    ['boolean', ['feature-state', 'hover'], false],
                    1,
                    0.8
                  ],
                  'text-color': [
                    'case',
                    ['boolean', ['feature-state', 'hover'], false],
                    '#15637F',
                    ['in', ['get', 'legacy'], ['literal', ['legacy', 'pilot']]],
                    '#294785',
                    '#000000'
                  ]
                }
              });
              setInitiativesLoaded(true);

              // Load Grant Layer
              // nest inside Initiatives loadImage callback to ensure initiatives is loaded.
              map.current.loadImage(
                'icons/marker-blue2-light.png',
                function (error, image) {
                  if (error) throw error;
                  map.current.addImage('grant-marker', image);
                  map.current.addLayer({
                    'id': 'grants',
                    'type': 'symbol',
                    'source': 'grants', // reference the data source
                    'layout': {
                      'visibility': (grantsChecked) ? 'visible' : 'none',
                      'icon-image': 'grant-marker',
                      'icon-allow-overlap': true,
                      // 'icon-ignore-placement': true,
                      'icon-size': 0.035,
                      'icon-anchor': 'bottom',
                      // get the title name from the source's "title" property
                      'text-field': ['get', 'title'],
                      'text-font': [
                        'Montserrat Regular',
                        'Open Sans Regular',
                        'Arial Unicode MS Regular'
                      ],
                      'text-size': {
                        "stops": [
                          [0, 0],
                          [5.9999, 0],
                          [6, 12]
                        ],
                      },
                      'text-offset': [0.7, -0.1],
                      'text-anchor': 'left',
                      // 'text-allow-overlap': true,
                      'text-ignore-placement': true,
                      'text-optional': true,
                    },
                    'paint': {
                      'icon-opacity': [
                        'case',
                        ['boolean', ['feature-state', 'hover'], false],
                        1,
                        0.8
                      ],
                      'text-color': [
                        'case',
                        ['boolean', ['feature-state', 'hover'], false],
                        '#000000',
                        '#4192a7'
                      ]
                    }
                  }, 'initiatives');
                  setGrantsLoaded(true);

                  // Load Expedition Layer
                  // nest inside Initiatives and Grants loadImage callbacks to ensure initiatives is loaded.
                  map.current.loadImage(
                    'icons/marker-blue2-dark.png',
                    function (error, image) {
                      if (error) throw error;
                      map.current.addImage('expedition-marker', image);
                      map.current.addLayer({
                        'id': 'expeditions',
                        'type': 'symbol',
                        'source': 'expeditions', // reference the data source
                        'layout': {
                          'visibility': (expeditionsChecked) ? 'visible' : 'none',
                          'icon-image': 'expedition-marker',
                          'icon-allow-overlap': true,
                          // 'icon-ignore-placement': true,
                          'icon-size': 0.035,
                          'icon-anchor': 'bottom',
                          // get the title name from the source's "title" property
                          'text-field': ["format",
                            ['get', 'location'], ' (', ['get', 'dates'], ')'
                          ],
                          'text-font': [
                            'Montserrat Regular',
                            'Open Sans Regular',
                            'Arial Unicode MS Regular'
                          ],
                          'text-size': {
                            "stops": [
                              [0, 0],
                              [5.9999, 0],
                              [6, 12]
                            ],
                          },
                          'text-offset': [0.7, -0.1],
                          'text-anchor': 'left',
                          // 'text-allow-overlap': true,
                          'text-ignore-placement': true,
                          'text-optional': true,
                        },
                        'paint': {
                          'icon-opacity': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            1,
                            0.8
                          ],
                          'text-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            '#000000',
                            '#153869'
                          ]
                        }
                      }, 'initiatives');
                      setExpeditionsLoaded(true);
                    },
                  );
                  // end load expedition layer

                },
              );
              // end load grant layer
            },
          );
          // end load legacy marker
        },
      );
      // end load initiatives layer

      map.current.on('mousemove', 'initiatives', (e) => {
        map.current.getCanvas().style.cursor = 'pointer';
        if (e.features.length > 0) {
          if (e.features[0].id !== hoverInitiativeId.current) {
            // setCategory('Initiative');
            // var title = e.features[0].properties.title;
            // setTitle(title);
          }

          if (hoverInitiativeId.current !== null) {
            map.current.removeFeatureState({
              source: "initiatives",
              id: hoverInitiativeId.current
            });
          }

          hoverInitiativeId.current = e.features[0].id;

          // Update the feature state for the feature under the mouse
          map.current.setFeatureState({
            source: 'initiatives',
            id: hoverInitiativeId.current,
          }, {
            hover: true
          });
        }
      });

      // When the mouse leaves the state-fill layer, update the feature state of the
      // previously hovered feature.
      map.current.on('mouseleave', 'initiatives', function () {
        map.current.getCanvas().style.cursor = '';
        if (hoverInitiativeId.current !== null) {
          map.current.setFeatureState({
            source: 'initiatives',
            id: hoverInitiativeId.current
          }, {
            hover: false
          });
        }
        hoverInitiativeId.current = null;
      });

      map.current.on('mousemove', 'expeditions', (e) => {
        map.current.getCanvas().style.cursor = 'pointer';
        if (e.features.length > 0) {
          if (e.features[0].id !== hoverExpeditionId.current) {
            // setCategory('ROC Grant');
            // setTitle(e.features[0].properties.title);
            // setDescription(e.features[0].properties.project_title);
            // setActionUrl(e.features[0].properties.link);
            // setFeaturedImg('images/maldives_thumb.jpg');
          }

          if (hoverExpeditionId.current !== null) {
            map.current.removeFeatureState({
              source: "expeditions",
              id: hoverExpeditionId.current
            });
            map.current.removeFeatureState({
              source: "expeditions-tracks",
              id: hoverExpeditionId.current
            });
          }

          hoverExpeditionId.current = e.features[0].id;

          // Update the feature state for the feature under the mouse
          map.current.setFeatureState({
            source: 'expeditions',
            id: hoverExpeditionId.current,
          }, {
            hover: true
          });
          map.current.setFeatureState({
            source: 'expeditions-tracks',
            id: hoverExpeditionId.current,
          }, {
            hover: true
          });
        }
      });

      // When the mouse leaves the state-fill layer, update the feature state of the
      // previously hovered feature.
      map.current.on('mouseleave', 'expeditions', function () {
        map.current.getCanvas().style.cursor = '';
        if (hoverExpeditionId.current !== null) {
          map.current.setFeatureState({
            source: 'expeditions',
            id: hoverExpeditionId.current
          }, {
            hover: false
          });
          map.current.setFeatureState({
            source: 'expeditions-tracks',
            id: hoverExpeditionId.current
          }, {
            hover: false
          });
        }
        hoverExpeditionId.current = null;
      });

      map.current.on('mousemove', 'grants', (e) => {
        map.current.getCanvas().style.cursor = 'pointer';
        if (e.features.length > 0) {
          if (e.features[0].id !== hoverGrantId.current) {
            // setCategory('ROC Grant');
            // setTitle(e.features[0].properties.title);
            // setDescription(e.features[0].properties.project_title);
            // setActionUrl(e.features[0].properties.link);
            // setFeaturedImg('images/maldives_thumb.jpg');
          }

          if (hoverGrantId.current !== null) {
            map.current.removeFeatureState({
              source: "grants",
              id: hoverGrantId.current
            });
          }

          hoverGrantId.current = e.features[0].id;

          // Update the feature state for the feature under the mouse
          map.current.setFeatureState({
            source: 'grants',
            id: hoverGrantId.current,
          }, {
            hover: true
          });
        }
      });

      // When the mouse leaves the state-fill layer, update the feature state of the
      // previously hovered feature.
      map.current.on('mouseleave', 'grants', function () {
        map.current.getCanvas().style.cursor = '';
        if (hoverGrantId.current !== null) {
          map.current.setFeatureState({
            source: 'grants',
            id: hoverGrantId.current
          }, {
            hover: false
          });
        }
        hoverGrantId.current = null;
      });

      var mypopup = null;

      const fillPopup = (e, feature, title) => {
        var coordinates = feature.geometry.coordinates.slice();
        var tip = title;
        if (!tip) {
          if (feature.properties.title) {
            tip = feature.properties.title;
          } else if (feature.properties.location) {
            tip = feature.properties.location;
          }
        }
        // Ensure that if the map is zoomed out such that multiple
        // copies of the feature are visible, the popup appears
        // over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        if (coordinates[1] > 90) {
          coordinates[1] = 89.9;
        }
        if (coordinates[1] < -90) {
          coordinates[1] = -89.9;
        }
        // Populate the popup and set its coordinates
        // based on the feature found.
        if (!mypopup) {
          mypopup = addPopup(tip, coordinates[0], coordinates[1]);
        }
        mypopup.setLngLat(coordinates)
          .setHTML(tip)
          .addTo(map.current);
      }

      const clickInitiative = (i) => {
        setCategory('Initiative');
        let title = i.properties.title;
        const theregex = /^The\s/i;
        title = title.replace(theregex, '<span class="sb-offset">The</span> ');
        const fsmregex = /^Federated States of\s/i;
        title = title.replace(fsmregex, '<span class="sb-offset">Federated States of</span> ');
        setTitle(title);
        setDates(null);
        setDescription(i.properties.description);
        setActionUrl(i.properties.link);
        if (i.properties.image_url) {
          setFeaturedImg(i.properties.image_url);
        } else {
          setFeaturedImg(null);
        }
      };

      const clickExpedition = (e) => {
        setCategory('Expedition');
        let title = e.properties.location;
        const theregex = /^The\s/i;
        title = title.replace(theregex, '<span class="sb-offset">The</span> ');
        const fsmregex = /^Federated States of\s/i;
        title = title.replace(fsmregex, '<span class="sb-offset">Federated States of</span> ');
        setTitle(title);
        setDates(e.properties.dates);
        setDescription(e.properties.description);
        setActionUrl(e.properties.link);
        if (e.properties.image_url) {
          setFeaturedImg(e.properties.image_url);
        } else {
          setFeaturedImg(null);
        }
      };

      const clickGrant = (g) => {
        setCategory('ROC Grant');
        setTitle(g.properties.title);
        setDescription(g.properties.project_title);
        setActionUrl(g.properties.link);
        setFeaturedImg('images/maldives_thumb.jpg');
        setDates(null);
      };

      map.current.on('mousemove', function (e) {
        let f = map.current.queryRenderedFeatures(e.point, { layers: ['initiatives', 'expeditions', 'grants'] });
        if (f.length) {
          // console.log('mousemove popup', f.length, f[0]);  //topmost feature
          if (f[0].layer && f[0].layer.id === 'initiatives') {
            fillPopup(e, f[0], f[0].properties.title);
          } else if (f[0].layer && f[0].layer.id === 'expeditions') {
            fillPopup(e, f[0], f[0].properties.location + ' (' + f[0].properties.dates + ')');
          } else if (f[0].layer && f[0].layer.id === 'grants') {
            fillPopup(e, f[0], f[0].properties.title);
          } else {
            // console.log('mousemove popup remove');  //topmost feature
            if (mypopup) {
              mypopup.remove();
            }
          }
        } else {
          // console.log('mousemove popup remove');  //topmost feature
          if (mypopup) {
            mypopup.remove();
          }
        }
      });

      map.current.on('click', function (e) {
        let f = map.current.queryRenderedFeatures(e.point, { layers: ['initiatives', 'expeditions', 'grants', 'eez-global-land-poly', 'eez-azores-land-poly'] });
        sidebarContentRef.current.scrollTop = 0;
        if (f.length) {
          // console.log('click', f.length, f[0].properties.title || f[0].properties.location);  //topmost feature
          if (f[0].layer && f[0].layer.id === 'initiatives') {
            clickInitiative(f[0]);
          } else if (f[0].layer && f[0].layer.id === 'expeditions') {
            clickExpedition(f[0]);
          } else if (f[0].layer && f[0].layer.id === 'grants') {
            clickGrant(f[0]);
          } else if (f[0].layer && (['eez-global-land-poly', 'eez-azores-land-poly'].includes(f[0].layer.id))) {
            let eezs = []
            if (f[0].layer.id === 'eez-azores-land-poly') {
              eezs = [f[0].properties.TERRITORY1]
            } else {
              eezs = [f[0].properties.ter1, f[0].properties.sov1]
            }
            let initiatives = map.current.queryRenderedFeatures({
              layers: ['initiatives'],
              filter: ['in', ['get', 'iso3'], ['literal', eezs]],
            });
            if (initiatives.length) {
              clickInitiative(initiatives[0]);
            } else {
              showWelcome();
            }
          }
        } else {
          // did not click on a feature, restore welcome message
          showWelcome();
        }
      });

      setAllLayersLoaded(true);

      // toggleLayer('initiatives', initiativesChecked);
      // toggleLayer('grants', grantsChecked);
      // toggleLayer('expeditions', expeditionsChecked);

    }); // map.current.on('load')
  }, []); // useEffect

  useEffect(() => {
    // console.log('Effect: eezs changed', eezIso3List);
    if (!allLayersLoaded) return;
    map.current.setFilter('eez-global-land-poly', eezFilter());
    map.current.setFilter('eez-global-land-line', eezFilter());
    map.current.setFilter('eez-azores-land-poly', eezFilter());
    map.current.setFilter('eez-azores-land-line', eezFilter());
    map.current.setPaintProperty('eez-global-land-poly', 'fill-color', eezFillStyle()['fill-color']);
    map.current.setPaintProperty('eez-azores-land-poly', 'fill-color', eezFillStyle()['fill-color']);
    map.current.setPaintProperty('eez-global-land-line', 'line-color', eezLineStyle()['line-color']);
    map.current.setPaintProperty('eez-azores-land-line', 'line-color', eezLineStyle()['line-color']);
  }, [eezIso3List, legacyIso3List, allLayersLoaded]);

  useEffect(() => {
    if (!initiativesLoaded) return;
    // console.log('INITIATIVES', map.current.getSource('initiatives')._data);
    let eezs = [];
    let legacies = [];
    fetch('./geojson/WEBMAP_waitt_institute_projects.geojson', {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })
      .then(resp => resp.json())
      .then((data) => {
        data.features.forEach((f) => {
          if (f.properties.iso3 !== '') {
            eezs.push(f.properties.iso3);
            if (f.properties.legacy === 'legacy' || f.properties.legacy === 'pilot') {
              legacies.push(f.properties.iso3);
            }
          }
        });
        // console.log('DATA', eezs, legacies);
        setEezIso3List(eezs);
        setLegacyIso3List(legacies);
      })
  }, [initiativesLoaded]);


  useEffect(() => {
    // console.log("initiativesChecked Effect:", initiativesChecked, initiativesLoaded);
    if (!initiativesLoaded || !allLayersLoaded) return;
    // console.log("      applying effect initiatives");
    toggleLayer('initiatives', initiativesChecked);
    toggleLayer('eez-global-land-poly', initiativesChecked);
    toggleLayer('eez-global-land-line', initiativesChecked);
    toggleLayer('eez-azores-land-poly', initiativesChecked);
    toggleLayer('eez-azores-land-line', initiativesChecked);

  },
    [initiativesChecked, initiativesLoaded, allLayersLoaded]
  );

  useEffect(() => {
    // console.log("grantsChecked Effect:", grantsChecked, grantsLoaded, allLayersLoaded);
    if (!grantsLoaded || !allLayersLoaded) return;
    // console.log("      applying effect grants");
    toggleLayer('grants', grantsChecked);
  },
    [grantsChecked, grantsLoaded, allLayersLoaded]
  );

  useEffect(() => {
    // console.log("expeditionsChecked Effect:", expeditionsChecked, expeditionsLoaded, allLayersLoaded);
    if (!expeditionsLoaded || !allLayersLoaded) return;
    // console.log("      applying effect expeditions");
    toggleLayer('expeditions', expeditionsChecked);
    toggleLayer('expeditions-tracks', expeditionsChecked);
  },
    [expeditionsChecked, expeditionsLoaded, allLayersLoaded]
  );

  const toggleLayer = (layer, show) => {
    if (show) {
      map.current.setLayoutProperty(
        layer,
        'visibility',
        'visible'
      );
    } else {
      map.current.setLayoutProperty(
        layer,
        'visibility',
        'none'
      );
    }
  }

  const handleInitiativesCheck = function () {
    var checked = !initiativesChecked;
    setInitiativesChecked(!initiativesChecked);
    // toggleLayer('initiatives', checked);
  }

  const handleGrantsCheck = function () {
    var checked = !grantsChecked;
    setGrantsChecked(!grantsChecked);
    // toggleLayer('grants', checked);
  }

  const handleExpeditionsCheck = function () {
    var checked = !expeditionsChecked;
    setExpeditionsChecked(!expeditionsChecked);
    // toggleLayer('expeditions', checked);
    // toggleLayer('expeditions-tracks', checked);
  }

  const handleZoomCheck = function () {
    var checked = !zoomChecked;
    setZoomChecked(!zoomChecked);
    if (checked) {
      map.current.scrollZoom.enable();
    } else {
      map.current.scrollZoom.disable();
    }
  }

  const containerClasses = function () {
    var classes = ['sb-container'];
    switch (category) {
      case 'Initiative':
        classes.push('initiative');
        break;
      case 'ROC Grant':
        classes.push('grant');
        break;
      case 'Expedition':
        classes.push('expedition');
        break;
      default:
      // code block
    }
    return (classes);
  }

  const showWelcome = () => {
    setCategory(welcomeCategory);
    setTitle(welcomeTitle);
    setDates(null);
    setDescription(welcomeDescription);
    setFeaturedImg(welcomeImg);
    setActionUrl(welcomeUrl);
  }

  let actionButton = <a className="sb-actionbutton" href={actionUrl} target="_parent">Learn More</a>;
  let featuredImage = <img className="sb-image" src={featuredImg} alt="" />;
  let datesElement = <div className="sb-dates">{dates}</div>;
  //////

  return (
    <div className="map-wrap">
      <div className="sidebar">
        <div className={containerClasses().join(" ")}>
          <div ref={sidebarContentRef} className="sb-content">
            <div className="sb-category">{category}</div>
            {featuredImg && featuredImage}
            <div className="sb-text">
              <div className="sb-title" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }}></div>
              {dates && datesElement}
              <div className="sb-description">
                {description}
              </div>
            </div>
          </div>
          <div className="sb-footer">
            {actionUrl && actionButton}
          </div>
        </div>
      </div>
      <div className="layerpanel">
        {/*<div className="lp-title">TYPE OF WORK</div>*/}
        <div className="lp-layer"><label className="switch"><input type="checkbox" id="initiatives-check" name="initiatives-check" value="show" defaultChecked={initiativesChecked} onChange={handleInitiativesCheck} /><span className="toggle"><span className="slider round"></span></span> Initiatives </label></div>
        <div className="lp-layer"><label className="switch"><input type="checkbox" id="grants-check" name="grants-check" value="show" defaultChecked={grantsChecked} onChange={handleGrantsCheck} /><span className="toggle"><span className="slider round"></span></span> ROC Grants</label></div>
        <div className="lp-layer"><label className="switch"><input type="checkbox" id="expeditions-check" name="expeditions-check" value="show" defaultChecked={expeditionsChecked} onChange={handleExpeditionsCheck} /><span className="toggle"><span className="slider round"></span></span> Expeditions</label></div>
        <hr />
        <div className="lp-layer lp-compact"><div className="lp-swatch eez"></div> Exclusive Economic Zones</div>
        <div className="lp-layer lp-compact"><div className="lp-swatch legacy"></div> Legacy Programs</div>
        <div className="lp-layer lp-compact"><div className="lp-swatch track"></div> Expedition Cruise Tracks</div>

        {/*
        <hr />
        <div className="lp-layer lp-zoom"><label className="switch"><input type="checkbox" id="zoom-check" name="zoom-check" value="enable" defaultChecked={zoomChecked} onChange={handleZoomCheck} /><span className="toggle"><span className="slider round"></span></span> Scroll to Zoom</label></div>
      */}
      </div>
      <div ref={mapContainer} className="map-container" />
    </div>
  );
}
